import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Button,
  ButtonGroup,
  Table,
  Badge,
  ProgressBar,
  OverlayTrigger,
  Tooltip as BsTooltip,
} from "react-bootstrap";
import {
  AlertTriangle,
  Globe,
  XCircle,
  CheckCircle,
  RefreshCw,
  DollarSign,
  TrendingUp,
  Activity,
} from "react-feather";
import {
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart as RechartsBarChart,
  Bar,
} from "recharts";
import { useCachedGet } from "../../api";
import { PageHeader } from "../../components/ui";
import { ReportStatCard } from "../../components/stat-card";
import {
  IOperationalDashboard,
  ICouponStatistics,
  ITemplateCaptureSummary,
  ICheckoutTechSeries,
} from "../../interfaces";
import { format, formatDistanceToNow } from "date-fns";

/** The three ways a run can be driven, and what each costs you. */
const MODE_META: Record<string, { label: string; bg: string; hint: string }> = {
  replay: {
    label: "session replay",
    bg: "success",
    hint: "Reuses a captured browser session — no login or discovery, so it is the fastest and cheapest browser path.",
  },
  api: {
    label: "API call",
    bg: "secondary",
    hint: "Applies coupons through the merchant's own API using stored credentials. No browser; typically under a second.",
  },
  walk: {
    label: "browser walk",
    bg: "primary",
    hint: "A full Chrome walk from the homepage. Slowest and where the AI spend goes — the fallback when there is no session or credentials.",
  },
  unknown: { label: "unrecorded", bg: "light", hint: "Ran before the path was recorded." },
};

/** Plain names for the run types, so the table reads as work rather than enum. */
const RUN_TYPE_LABELS: Record<string, string> = {
  live: "Coupon test",
  session: "Session replay",
  capture: "Template capture",
  audit: "Audit",
};

const pct = (n: number, d: number): string =>
  d > 0 ? `${Math.round((n / d) * 100)}%` : "—";

const num = (n: number): string => n.toLocaleString();

interface ISegment {
  label: string;
  value: number;
  color: string;
}

/**
 * The coupon verdict split as one bar plus a legend, instead of five badge
 * rows — the proportions are the point, and they are not readable as numbers.
 */
const OutcomeBar: React.FC<{ segments: ISegment[] }> = ({ segments }) => {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  return (
    <>
      <div className="d-flex rounded overflow-hidden" style={{ height: 10 }}>
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <OverlayTrigger
              key={s.label}
              placement="top"
              overlay={
                <BsTooltip id={`seg-${s.label}`}>
                  {`${s.label}: ${num(s.value)} (${pct(s.value, total)})`}
                </BsTooltip>
              }
            >
              <div
                style={{
                  width: `${(s.value / total) * 100}%`,
                  backgroundColor: s.color,
                }}
              />
            </OverlayTrigger>
          ))}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-2">
        {segments.map((s) => (
          <span
            key={s.label}
            className="d-flex align-items-center text-muted"
            style={{ fontSize: "0.78rem" }}
          >
            <span
              className="rounded-circle me-1 d-inline-block flex-shrink-0"
              style={{ width: 8, height: 8, backgroundColor: s.color }}
            />
            {s.label}
            <strong className="text-dark ms-1">{num(s.value)}</strong>
            <span className="ms-1">({pct(s.value, total)})</span>
          </span>
        ))}
      </div>
    </>
  );
};

/** Label / value pair for the dense fact lists inside the capture panel. */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div
    className="d-flex justify-content-between align-items-baseline"
    style={{ fontSize: "0.8rem" }}
  >
    <span className="text-muted">{label}</span>
    <span>{children}</span>
  </div>
);

export const Dashboard: React.FC = () => {
  // Default to "today" mode (from midnight UTC onwards)
  const [filterMode, setFilterMode] = React.useState<
    "today" | "pastDays" | "custom"
  >("today");
  const [pastDays, setPastDays] = React.useState(1);
  const [startDate] = React.useState<Date | null>(null);
  const [endDate] = React.useState<Date | null>(null);

  // Build query params based on filter mode
  const buildQueryParams = () => {
    if (filterMode === "today") {
      // No params needed - backend defaults to today from midnight UTC
      return "";
    } else if (filterMode === "pastDays") {
      return `pastDays=${pastDays}`;
    } else if (filterMode === "custom" && startDate) {
      const params = new URLSearchParams();
      params.append("startDate", startDate.toISOString());
      if (endDate) {
        params.append("endDate", endDate.toISOString());
      }
      return params.toString();
    }
    return "";
  };

  // The capture panel follows the same window the rest of the page is showing.
  const captureWindowHours =
    filterMode === "today" ? 24 : Math.max(1, pastDays) * 24;

  const queryParams = buildQueryParams();
  const data = useCachedGet<IOperationalDashboard>(
    `/reporting/summary${queryParams ? `?${queryParams}` : ""}`
  );
  const couponStatsData = useCachedGet<ICouponStatistics>(
    `/reporting/coupon-statistics${queryParams ? `?${queryParams}` : ""}`
  );
  // "Today" has to be sent explicitly as midnight-to-now. The other endpoints
  // default to it server-side when given no params, but this one would fall
  // back to a rolling 24 hours — which reported yesterday's proxy outage as
  // today's dominant failure.
  const captureRange = (() => {
    if (filterMode === "today") {
      const midnight = new Date();
      midnight.setUTCHours(0, 0, 0, 0);
      return `&startDate=${midnight.toISOString()}`;
    }
    if (filterMode === "custom" && startDate) {
      return `&startDate=${startDate.toISOString()}${
        endDate ? `&endDate=${endDate.toISOString()}` : ""
      }`;
    }
    return ""; // pastDays is already expressed by sinceHours
  })();

  const techData = useCachedGet<ICheckoutTechSeries>(
    `/reporting/checkout-tech-series?days=${
      filterMode === "today" ? 1 : Math.max(1, pastDays)
    }${captureRange}`
  );

  const captureData = useCachedGet<ITemplateCaptureSummary>(
    `/journey-test-results/capture-summary?sinceHours=${captureWindowHours}${captureRange}`
  );

  const handlePeriodChange = (days: number) => {
    setFilterMode("pastDays");
    setPastDays(days);
  };

  if (data.loading) {
    return (
      <Container fluid className="mt-3">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (data.error || !data.data) {
    return (
      <Container fluid className="mt-3">
        <div className="text-center text-danger">
          <AlertTriangle size={48} className="mb-3" />
          <h4>Failed to load dashboard data</h4>
          <p>{data.error?.message || "An unknown error occurred"}</p>
          <Button variant="outline-primary" onClick={() => data.reload()}>
            Retry
          </Button>
        </div>
      </Container>
    );
  }

  const dashboard = data.data;
  const capture = captureData.data;
  const coupons = couponStatsData.data;
  const isRefreshing =
    data.loading ||
    couponStatsData.loading ||
    captureData.loading ||
    techData.loading;
  const {
    metrics,
    timeSeriesData,
    countryBreakdown,
    failedMerchants,
    failurePatterns,
    recentActivity,
  } = dashboard;

  // Format time series data for charts
  const formattedTimeSeriesData = timeSeriesData.map((point) => {
    const pointDate = new Date(point.timestamp);
    // Use hourly format for today/24h, daily for longer periods
    const isShortPeriod =
      filterMode === "today" || (filterMode === "pastDays" && pastDays <= 1);
    return {
      ...point,
      time: isShortPeriod
        ? format(pointDate, "HH:mm")
        : format(pointDate, "MMM dd"),
    };
  });

  // One row per bucket with a column per technology, which is the shape both
  // recharts series want. Cost and success are separate charts: stacking a rate
  // would be meaningless, and a shared axis would bury the cheap platforms.
  const tech = techData.data;
  const TECH_COLOURS: Record<string, string> = {
    shopify: "#96bf48",
    woocommerce: "#7f54b3",
    magento: "#ee672f",
    bigcommerce: "#0d52ff",
    salesforce: "#00a1e0",
    other: "#dc3545",
    unset: "#adb5bd",
  };
  const techColour = (t: string) => TECH_COLOURS[t] ?? "#6c757d";
  const techBucketLabel = (iso: string) =>
    format(new Date(iso), tech?.bucketHours === 1 ? "HH:mm" : "MMM dd");

  const techCostData = (tech?.points ?? []).map((p) => {
    const row: any = { time: techBucketLabel(p.bucket) };
    (tech?.technologies ?? []).forEach((t) => {
      row[t] = Number((p.tech[t]?.cost ?? 0).toFixed(2));
    });
    return row;
  });

  const techSuccessData = (tech?.points ?? []).map((p) => {
    const row: any = { time: techBucketLabel(p.bucket) };
    (tech?.technologies ?? []).forEach((t) => {
      const cell = p.tech[t];
      const judged = (cell?.succeeded ?? 0) + (cell?.failed ?? 0);
      // Null rather than 0 so recharts breaks the line instead of drawing a
      // drop to zero for a bucket where the platform simply did not run.
      row[t] = judged ? Math.round((100 * (cell?.succeeded ?? 0)) / judged) : null;
    });
    return row;
  });

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  const getStatusBadge = (consecutiveFailures: number) => {
    if (consecutiveFailures >= 5) return <Badge bg="danger">Critical</Badge>;
    if (consecutiveFailures >= 3) return <Badge bg="warning">Warning</Badge>;
    return <Badge bg="secondary">Minor</Badge>;
  };

  // The queue reads as one line rather than a tile each — it is only ever
  // glanced at to answer "is anything moving".
  const queueSummary =
    (recentActivity.byRunType ?? [])
      .map(
        (r) =>
          `${RUN_TYPE_LABELS[r.runType] ?? r.runType} ${r.inProgress}${
            r.queued > 0 ? ` +${r.queued}` : ""
          }`
      )
      .join(" · ") || "nothing queued";

  const RANGES: { label: string; active: boolean; onClick: () => void }[] = [
    { label: "Today", active: filterMode === "today", onClick: () => setFilterMode("today") },
    { label: "24h", active: filterMode === "pastDays" && pastDays === 1, onClick: () => handlePeriodChange(1) },
    { label: "7d", active: filterMode === "pastDays" && pastDays === 7, onClick: () => handlePeriodChange(7) },
    { label: "30d", active: filterMode === "pastDays" && pastDays === 30, onClick: () => handlePeriodChange(30) },
  ];

  return (
    <Container fluid className="mt-3">
      <PageHeader
        title="Operational Dashboard"
        description={`${format(
          new Date(dashboard.period.startDate),
          "MMM dd"
        )} – ${format(
          new Date(dashboard.period.endDate),
          "MMM dd, yyyy"
        )}${
          data.fetchedAt
            ? ` · data from ${formatDistanceToNow(
                new Date(data.fetchedAt)
              )} ago · last task ${
                recentActivity.lastTaskCompleted
                  ? formatDistanceToNow(
                      new Date(recentActivity.lastTaskCompleted),
                      { addSuffix: true }
                    )
                  : "—"
              } · ${queueSummary}`
            : ""
        }`}
        actions={
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isRefreshing}
              onClick={() => {
                data.reload();
                couponStatsData.reload();
                captureData.reload();
                techData.reload();
              }}
              title="Fetch fresh data now, ignoring the 5-minute cache"
            >
              <RefreshCw size={14} className="me-1" />
              {isRefreshing ? "Refreshing…" : "Refresh"}
            </Button>
            <ButtonGroup size="sm">
              {RANGES.map((r) => (
                <Button
                  key={r.label}
                  variant={r.active ? "primary" : "outline-primary"}
                  onClick={r.onClick}
                >
                  {r.label}
                </Button>
              ))}
            </ButtonGroup>
          </>
        }
      />

      {/* One KPI strip — the old hero card and the four tiles said the same thing twice */}
      <Row className="g-2 mb-3">
        <ReportStatCard
          label="Tasks"
          value={num(metrics.totalTasksRun)}
          sub={`${formatPercentage(metrics.taskSuccessRate)} success`}
        />
        <ReportStatCard
          label="Coupons tested"
          value={num(metrics.totalCouponsTestedAcrossTasks)}
          sub={`${formatPercentage(metrics.couponSuccessRate)} success`}
        />
        <ReportStatCard
          label="Merchants"
          value={num(metrics.totalMerchantsInPeriod)}
          sub={`${countryBreakdown.length} countries`}
        />
        <ReportStatCard
          label="LLM cost"
          value={formatCurrency(metrics.totalLLMCostAcrossTasks)}
          color="success"
          sub={`avg ${formatCurrency(metrics.averageLLMCostPerTask)} / task`}
        />
        <ReportStatCard
          label="Running"
          value={num(recentActivity.tasksInProgress)}
          color={recentActivity.tasksInProgress > 0 ? "primary" : "dark"}
          sub={queueSummary}
        />
        {coupons && (
          <ReportStatCard
            label="Unique coupons"
            value={num(coupons.uniqueCouponsTested)}
            sub={`${num(coupons.merchantsWithoutCoupons)} merchants had none`}
          />
        )}
      </Row>

      {/* Coupon verdicts */}
      {coupons && (
        <Card className="mb-3">
          <Card.Body className="py-3">
            <div className="d-flex justify-content-between align-items-baseline mb-2">
              <strong style={{ fontSize: "0.9rem" }}>Coupon verdicts</strong>
              <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                {num(coupons.uniqueMerchantsWithTasks)} merchants ·{" "}
                {num(coupons.uniqueCouponsTested)} coupons ·{" "}
                {format(new Date(coupons.period.startDate), "MMM dd")} –{" "}
                {format(new Date(coupons.period.endDate), "MMM dd")}
              </span>
            </div>
            <OutcomeBar
              segments={[
                { label: "Successful", value: coupons.successfulCoupons, color: "#28a745" },
                { label: "Not applicable", value: coupons.notApplicableCoupons, color: "#ffc107" },
                { label: "Confirmed fails", value: coupons.couponsWithConfirmedFailures, color: "#dc3545" },
                { label: "Unrecognised fails", value: coupons.couponsWithUnrecognisedFailures, color: "#343a40" },
                { label: "System errors", value: coupons.couponsWithSystemErrors, color: "#6c757d" },
              ]}
            />
          </Card.Body>
        </Card>
      )}

      {/* What actually ran, split by why and how */}
      <Card className="mb-3">
        <Card.Header className="py-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <strong style={{ fontSize: "0.9rem" }}>
            <Activity size={15} className="me-2" />
            What ran
          </strong>
          <div className="d-flex align-items-center gap-3" style={{ fontSize: "0.78rem" }}>
            {(["replay", "api", "walk"] as const).map((m) => (
              <OverlayTrigger
                key={m}
                placement="bottom"
                overlay={<BsTooltip id={`legend-${m}`}>{MODE_META[m].hint}</BsTooltip>}
              >
                <span className="text-muted" style={{ cursor: "help" }}>
                  <Badge bg={MODE_META[m].bg} className="me-1">
                    &nbsp;
                  </Badge>
                  {MODE_META[m].label}
                </span>
              </OverlayTrigger>
            ))}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Run type</th>
                <th className="text-end">Tasks</th>
                <th className="text-end">Succeeded</th>
                <th className="text-end">Failed</th>
                <th className="text-end">No coupons</th>
                <th className="text-end">Success</th>
                <th className="text-end">Median</th>
                <th className="text-end">LLM cost</th>
              </tr>
            </thead>
            <tbody>
              {(metrics.byRunType ?? []).map((r) => (
                <tr key={`${r.runType}-${r.executionMode}`}>
                  <td>
                    <Badge bg={MODE_META[r.executionMode]?.bg ?? "light"} className="me-2">
                      &nbsp;
                    </Badge>
                    <strong>{RUN_TYPE_LABELS[r.runType] ?? r.runType}</strong>
                  </td>
                  <td className="text-end">{num(r.tasks)}</td>
                  <td className="text-end text-success">{num(r.successful)}</td>
                  <td className="text-end text-danger">{num(r.failed)}</td>
                  <td className="text-end text-muted">
                    {r.nothingToTest ? num(r.nothingToTest) : "—"}
                  </td>
                  <td className="text-end">{formatPercentage(r.successRate)}</td>
                  <td className="text-end text-muted">
                    {r.medianDurationMs == null
                      ? "—"
                      : `${Math.round(r.medianDurationMs / 1000)}s`}
                  </td>
                  <td className="text-end">${r.llmCost.toFixed(2)}</td>
                </tr>
              ))}
              {!(metrics.byRunType ?? []).length && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-3">
                    Nothing ran in this window.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Template captures — three panels, one card */}
      {capture && (
        <Card className="mb-3">
          <Card.Header className="py-2">
            <strong style={{ fontSize: "0.9rem" }}>
              <Globe size={15} className="me-2" />
              Template captures
            </strong>
          </Card.Header>
          <Card.Body className="py-3">
            <Row className="g-4">
              <Col lg={4}>
                <div className="d-flex justify-content-between align-items-baseline">
                  <h4 className="mb-0">
                    {pct(capture.coverage.everCaptured, capture.coverage.scheduledMerchants)}
                  </h4>
                  <span className="text-muted" style={{ fontSize: "0.78rem" }}>
                    {num(capture.coverage.everCaptured)} of{" "}
                    {num(capture.coverage.scheduledMerchants)} ever captured
                  </span>
                </div>
                <ProgressBar
                  now={
                    (capture.coverage.everCaptured /
                      Math.max(1, capture.coverage.scheduledMerchants)) *
                    100
                  }
                  className="my-2"
                  style={{ height: 6 }}
                />
                <Fact label="Maps built">
                  {num(capture.coverage.mapsBuilt)}
                  <span className="text-muted ms-2">
                    {capture.coverage.mapsByView.mobile} mobile ·{" "}
                    {capture.coverage.mapsByView.desktop} desktop
                  </span>
                </Fact>
                <Fact label="Due a capture now">
                  <Badge bg={capture.coverage.dueNow > 0 ? "warning" : "success"} text="dark">
                    {num(capture.coverage.dueNow)}
                  </Badge>
                </Fact>
                <Fact label="Captured in window">
                  {num(capture.coverage.capturedInWindow)}
                </Fact>
                <Fact label="Attempted / completed">
                  {num(capture.capturesAttempted)} / {num(capture.capturesCompleted)}
                </Fact>
              </Col>

              <Col lg={4}>
                <div className="d-flex gap-4 mb-2">
                  <div>
                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>Pass</div>
                    <h5 className="text-success mb-0">{capture.outcomes.pass}</h5>
                  </div>
                  <div>
                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>Fail</div>
                    <h5 className="text-danger mb-0">{capture.outcomes.fail}</h5>
                  </div>
                  <div>
                    <div className="text-muted" style={{ fontSize: "0.72rem" }}>Not tested</div>
                    <h5 className="text-muted mb-0">{capture.outcomes.notTested}</h5>
                  </div>
                </div>
                <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                  Legs failing most often
                </div>
                {capture.outcomes.topFailingNodes.map((n) => (
                  <div key={n.node} className="mt-1">
                    <div
                      className="d-flex justify-content-between"
                      style={{ fontSize: "0.78rem" }}
                    >
                      <span className="text-truncate me-2">{n.node}</span>
                      <strong>{n.count}</strong>
                    </div>
                    <ProgressBar
                      now={
                        (n.count /
                          Math.max(1, capture.outcomes.topFailingNodes[0]?.count ?? 1)) *
                        100
                      }
                      variant="danger"
                      style={{ height: 3 }}
                    />
                  </div>
                ))}
                {!capture.outcomes.topFailingNodes.length && (
                  <div className="text-muted small">No failing legs in this window.</div>
                )}
              </Col>

              <Col lg={4}>
                <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>
                  Where captures stop
                </div>
                {capture.whereRunsDie.map((w) => {
                  const total = capture.whereRunsDie.reduce((a, x) => a + x.count, 0);
                  return (
                    <div key={w.stage} className="mt-1">
                      <div
                        className="d-flex justify-content-between"
                        style={{ fontSize: "0.78rem" }}
                      >
                        <span className="text-truncate me-2">{w.stage}</span>
                        <span className="text-muted">
                          {w.count} · {pct(w.count, total)}
                        </span>
                      </div>
                      <ProgressBar
                        now={(w.count / Math.max(1, capture.whereRunsDie[0]?.count ?? 1)) * 100}
                        variant="warning"
                        style={{ height: 3 }}
                      />
                    </div>
                  );
                })}
                {!capture.whereRunsDie.length && (
                  <div className="text-muted small">No failed captures in this window.</div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* AI spend and success, per checkout technology */}
      <Card className="mb-3">
        <Card.Header className="py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <strong style={{ fontSize: "0.9rem" }}>
            <DollarSign size={15} className="me-2" />
            AI spend by checkout technology
          </strong>
          <span className="text-muted" style={{ fontSize: "0.78rem" }}>
            where the money goes, and whether it is buying anything
          </span>
        </Card.Header>
        <Card.Body className="py-3">
          {techData.loading && (
            <div className="text-center py-4">
              <Spinner />
            </div>
          )}
          {!techData.loading && !(tech?.points ?? []).length && (
            <div className="text-center text-muted py-4">
              Nothing ran in this window.
            </div>
          )}
          {!techData.loading && !!(tech?.points ?? []).length && (
            <Row className="g-3">
              <Col lg={7}>
                <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>
                  AI spend ($)
                </div>
                <div style={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={techCostData} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: any, n: any) => [`$${v}`, n]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {(tech?.technologies ?? []).map((t) => (
                        <Bar key={t} dataKey={t} stackId="cost" fill={techColour(t)} />
                      ))}
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col lg={5}>
                <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>
                  Success rate (%) — runs that reached a verdict
                </div>
                <div style={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={techSuccessData} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: any) => `${v}%`} />
                      {(tech?.technologies ?? []).map((t) => (
                        <Line
                          key={t}
                          type="monotone"
                          dataKey={t}
                          stroke={techColour(t)}
                          dot={false}
                          connectNulls={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col xs={12}>
                <Table size="sm" responsive className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Checkout technology</th>
                      <th className="text-end">Tasks</th>
                      <th className="text-end">AI cost</th>
                      <th className="text-end">Cost / run</th>
                      <th className="text-end">Success</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tech?.technologies ?? []).map((t) => {
                      const v = tech!.totals[t];
                      return (
                        <tr key={t}>
                          <td>
                            <Badge style={{ backgroundColor: techColour(t) }} className="me-2">
                              &nbsp;
                            </Badge>
                            {t}
                          </td>
                          <td className="text-end">{num(v.tasks)}</td>
                          <td className="text-end">${v.cost.toFixed(2)}</td>
                          <td className="text-end">${v.costPerRun.toFixed(3)}</td>
                          <td className="text-end">{Math.round(v.successRate)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Activity and geography */}
      <Row className="g-3 mb-3">
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <strong style={{ fontSize: "0.9rem" }}>
                <TrendingUp size={15} className="me-2" />
                Activity over time
              </strong>
            </Card.Header>
            <Card.Body className="py-2">
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formattedTimeSeriesData} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="tasksCompleted" stroke="#8884d8" name="Tasks completed" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="noCouponsTasks" stroke="#6c757d" name="No coupons" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="failedTasks" stroke="#d49ca7" name="Failed" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="successfulTasks" stroke="#36abff" name="Successful" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="couponsTestedInHour" stroke="#82ca9d" name="Coupons tested" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <strong style={{ fontSize: "0.9rem" }}>
                <Globe size={15} className="me-2" />
                Tasks by country
              </strong>
            </Card.Header>
            <Card.Body className="py-2">
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsBarChart data={countryBreakdown.slice(0, 8)} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="countryCode" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value, name) => [value, name === "tasksRun" ? "Tasks" : name]}
                    />
                    <Bar dataKey="tasksRun" fill="#8884d8" />
                  </RechartsBarChart>
                </ResponsiveContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Failures */}
      <Row className="g-3 mb-3">
        <Col lg={5}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <strong style={{ fontSize: "0.9rem" }}>
                <XCircle size={15} className="me-2" />
                Recent failed merchants
              </strong>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 280, overflowY: "auto" }}>
              {failedMerchants.length > 0 ? (
                <Table responsive size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Geo</th>
                      <th>Status</th>
                      <th className="text-end">Last fail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedMerchants.slice(0, 10).map((merchant) => (
                      <tr key={merchant.merchantId}>
                        <td style={{ maxWidth: 170 }}>
                          <div className="fw-semibold text-truncate">
                            {merchant.merchantName}
                          </div>
                          <div
                            className="text-muted text-truncate"
                            style={{ fontSize: "0.72rem" }}
                          >
                            {merchant.domain}
                          </div>
                        </td>
                        <td>
                          <Badge bg="secondary">{merchant.countryCode}</Badge>
                        </td>
                        <td>
                          {getStatusBadge(merchant.consecutiveFailures)}{" "}
                          <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                            ×{merchant.consecutiveFailures}
                          </span>
                        </td>
                        <td className="text-end text-muted" style={{ fontSize: "0.72rem" }}>
                          {formatDistanceToNow(new Date(merchant.lastTaskTime), {
                            addSuffix: true,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  <CheckCircle size={24} className="mb-2" />
                  <div className="small">No failed merchants in this period</div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="h-100">
            <Card.Header className="py-2">
              <strong style={{ fontSize: "0.9rem" }}>
                <AlertTriangle size={15} className="me-2" />
                Common failure patterns
              </strong>
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: 280, overflowY: "auto" }}>
              {failurePatterns.length > 0 ? (
                <Table responsive size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Error</th>
                      <th className="text-end">Count</th>
                      <th className="text-end">Share</th>
                      <th className="text-end">Merchants</th>
                      <th>Seen</th>
                      <th>Top geos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failurePatterns.slice(0, 8).map((pattern, index) => (
                      <tr key={index}>
                        <td style={{ maxWidth: 260 }}>
                          <div className="text-truncate" title={pattern.error}>
                            {pattern.error}
                          </div>
                        </td>
                        <td className="text-end">
                          <Badge bg="danger">{pattern.count}</Badge>
                        </td>
                        <td className="text-end">{formatPercentage(pattern.percentage)}</td>
                        <td className="text-end">{pattern.affectedMerchants}</td>
                        <td className="text-muted" style={{ fontSize: "0.72rem" }}>
                          {format(new Date(pattern.firstOccurrence), "MMM dd")} –{" "}
                          {format(new Date(pattern.lastOccurrence), "MMM dd")}
                        </td>
                        <td>
                          {Object.entries(pattern.countryBreakdown)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 3)
                            .map(([country, count]) => (
                              <Badge key={country} bg="secondary" className="me-1">
                                {country}: {count}
                              </Badge>
                            ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  <CheckCircle size={24} className="mb-2" />
                  <div className="small">No failure patterns detected</div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};
